import { FC, useContext, useEffect, useRef } from 'react';
import Gleap from 'gleap';
import { pick } from 'lodash';
import { AuthContext } from 'providers/MPAuthContextProvider';
import { useRoles } from 'hooks';
import { getIdentifiers } from 'ui/helpers';

/**
 * Component that identifies the current user to Gleap for support ticket context.
 * Must be rendered as a descendant of both AuthContext and RoleProvider.
 */
export const GleapIdentifier: FC = () => {
  const {
    auth
  } = useContext(AuthContext);
  const role = useRoles();
  const hasIdentified = useRef(false);
  const gleapListenerRegisteredRef = useRef(false);
  useEffect(() => {
    if (!auth.token || !role) {
      return;
    }
    const identifyUser = () => {
      if (hasIdentified.current) {
        return;
      }
      const pii = pick(auth, ['email', 'entityId', 'entityName', 'fullName', 'id']);
      const gleapKey = [getIdentifiers().server, pii.id].join('-');
      Gleap.identify(gleapKey, {
        name: pii.fullName,
        email: pii.email,
        companyName: pii.entityName,
        companyId: pii.entityId?.toString()
      });
      Gleap.updateContact({
        role,
        ...segmentsForUserId(pii.id)
      } as any);
      hasIdentified.current = true;
    };

    // Register listener for when Gleap initializes (only once)
    if (!gleapListenerRegisteredRef.current) {
      Gleap.on('initialized', identifyUser);
      gleapListenerRegisteredRef.current = true;
    }

    // Also try immediately in case Gleap is already initialized
    identifyUser();
    return () => {
      hasIdentified.current = false;
    };
  }, [auth.token, auth.email, auth.fullName, auth.entityId, auth.entityName, auth.id, role]);
  return null;
};

/**
 * Identify users as belonging to 50%, 33% or 25% sample groups, based off of userId.
 * By comparing against the current month, each group will change throughout the year.
 */
function segmentsForUserId(userId: number) {
  const currMonth = new Date().getMonth();
  return {
    inHalfOfUsers: userId % 2 === currMonth % 2 ? 'true' : 'false',
    inThirdOfUsers: userId % 3 === currMonth % 3 ? 'true' : 'false',
    inQuarterOfUsers: userId % 4 === currMonth % 4 ? 'true' : 'false'
  };
}
export default GleapIdentifier;